/**
 * BookingsScreen.jsx
 * User appointments with REAL data from backend
 * Includes Waitlist tab for customer waitlist entries
 */
import React, { useState, useEffect } from 'react';
import { Calendar, RefreshCw, AlertCircle, ListChecks, Clock, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return `${days[date.getDay()]}, ${date.getDate()}.${date.getMonth() + 1}.`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

export const BookingsScreen = ({ 
  appointments = [], 
  upcomingAppointments = [],
  onCancelAppointment,
  onRefresh,
  portalToken
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [cancelling, setCancelling] = useState(null);
  const [cancellingSeriesId, setCancellingSeriesId] = useState(null);
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return { bg: 'bg-green-500/20', text: 'text-green-400', label: t('bookings.status_confirmed') };
      case 'pending':
        return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: t('bookings.status_pending') };
      case 'cancelled':
        return { bg: 'bg-red-500/20', text: 'text-red-400', label: t('bookings.status_cancelled') };
      case 'completed':
        return { bg: 'bg-blue-500/20', text: 'text-blue-400', label: t('bookings.status_completed') };
      default:
        return { bg: 'bg-zinc-700', text: 'text-zinc-400', label: status };
    }
  };

  const formatTimePreferences = (prefs) => {
    if (!prefs || prefs.length === 0 || prefs.includes('any')) return t('bookings.anytime');
    const labels = { morning: t('bookings.time_morning'), midday: t('bookings.time_midday'), evening: t('bookings.time_evening') };
    return prefs.map(p => labels[p] || p).join(', ');
  };
  
  // Waitlist state
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  
  // Filter past appointments
  const now = new Date();
  const pastAppointments = appointments.filter(apt => new Date(apt.start_time) < now);
  
  // Fetch waitlist
  const fetchWaitlist = async () => {
    if (!portalToken) return;
    setWaitlistLoading(true);
    try {
      const response = await axios.get(`${API}/portal/waitlist`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setWaitlistEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    } finally {
      setWaitlistLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'waitlist') {
      fetchWaitlist();
    }
  }, [activeTab, portalToken]);

  const handleDeleteWaitlistEntry = async (entryId) => {
    setDeletingEntryId(entryId);
    try {
      await axios.delete(`${API}/portal/waitlist/${entryId}`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success(t('bookings.removed_from_waitlist'));
      fetchWaitlist();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setDeletingEntryId(null);
    }
  };
  
  // Get appointments based on active tab
  const displayAppointments = activeTab === 'upcoming' ? upcomingAppointments : pastAppointments;

  const handleCancel = async (appointmentId) => {
    if (!onCancelAppointment) return;
    setCancelling(appointmentId);
    await onCancelAppointment(appointmentId);
    setCancelling(null);
  };

  const handleCancelSeries = async (seriesId) => {
    if (!portalToken) return;
    const seriesApts = upcomingAppointments.filter(a => a.series_id === seriesId);
    if (!window.confirm(t('bookings.cancel_series_confirm'))) return;
    setCancellingSeriesId(seriesId);
    try {
      await axios.post(
        `${API}/portal/appointments/series/${seriesId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success(t('bookings.cancelled'));
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('bookings.cancel_failed'));
    } finally {
      setCancellingSeriesId(null);
    }
  };

  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const buildCalendarEventLinks = (appointment) => {
    if (!appointment) return null;
    if (!portalToken) return null;
    const start = appointment.start_time ? new Date(appointment.start_time) : null;
    if (!start) return null;
    const end = appointment.end_time ? new Date(appointment.end_time) : new Date(start.getTime() + 60 * 60000);
    const titleBase = appointment.service_name || 'Training';
    const title = appointment.trainer_name ? `${titleBase} mit ${appointment.trainer_name}` : titleBase;
    const details = [`Training: ${titleBase}`, appointment.trainer_name ? `Trainer: ${appointment.trainer_name}` : null].filter(Boolean).join('\n');

    const googleDates = `${formatGoogleDate(start)}/${formatGoogleDate(end)}`;
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${googleDates}&details=${encodeURIComponent(details)}`;
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdt=${encodeURIComponent(start.toISOString())}&enddt=${encodeURIComponent(end.toISOString())}&body=${encodeURIComponent(details)}`;

    const icsUrl = `${API}/portal/appointments/${appointment.id}/calendar?token=${portalToken}`;
    const appleUrl = icsUrl.replace('https://', 'webcal://').replace('http://', 'webcal://');

    return { googleUrl, outlookUrl, appleUrl };
  };

  const buildCalendarSeriesLinks = (seriesId) => {
    if (!seriesId) return null;
    if (!portalToken) return null;
    const icsUrl = `${API}/portal/appointments/series/${seriesId}/calendar?token=${portalToken}`;
    const appleUrl = icsUrl.replace('https://', 'webcal://').replace('http://', 'webcal://');
    const googleUrl = `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(icsUrl)}`;
    const outlookUrl = `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(icsUrl)}`;
    return { googleUrl, outlookUrl, appleUrl };
  };

  // Calendar subscription state
  const [calendarSubscribed, setCalendarSubscribed] = useState(() => {
    try { return localStorage.getItem('strongfit_calendar_subscribed') === 'true'; } catch { return false; }
  });

  const handleSubscribe = (type) => {
    // Build a general subscription URL for all appointments
    if (!portalToken) return;
    const baseIcsUrl = `${API}/portal/appointments/calendar-feed?token=${portalToken}`;
    const appleUrl = baseIcsUrl.replace('https://', 'webcal://').replace('http://', 'webcal://');
    
    if (type === 'google') {
      window.open(`https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(baseIcsUrl)}`, '_blank');
    } else if (type === 'outlook') {
      window.open(`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(baseIcsUrl)}`, '_blank');
    } else {
      window.location.href = appleUrl;
    }
    setCalendarSubscribed(true);
    try { localStorage.setItem('strongfit_calendar_subscribed', 'true'); } catch {}
    toast.success(t('bookings.cancelled'));
  };

  // Count waiting entries
  const waitingCount = waitlistEntries.filter(e => e.status === 'waiting').length;

  return (
    <div className="p-4 space-y-3">
      <div className="flex justify-between items-center pt-2">
        <h1 className="text-white text-base font-bold">{t('bookings.title')}</h1>
        {onRefresh && (
          <button 
            onClick={onRefresh}
            className="p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700"
          >
            <RefreshCw className="w-4 h-4 text-zinc-400" />
          </button>
        )}
      </div>
      
      {/* Tabs - Grid Layout für bessere Übersicht */}
      <div className="grid grid-cols-3 gap-2">
        <button 
          onClick={() => setActiveTab('upcoming')}
          className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-all ${
            activeTab === 'upcoming' 
              ? 'bg-yellow-500 text-black' 
              : 'bg-zinc-800/80 text-zinc-300 hover:bg-zinc-700'
          }`}
        >
          <Calendar className={`w-5 h-5 mb-1 ${activeTab === 'upcoming' ? 'text-black' : 'text-yellow-400'}`} />
          <span className="text-xs font-medium">{t('bookings.upcoming')}</span>
          <span className={`text-[10px] mt-0.5 ${activeTab === 'upcoming' ? 'text-black/70' : 'text-zinc-500'}`}>
            ({upcomingAppointments.length})
          </span>
        </button>
        <button 
          onClick={() => setActiveTab('past')}
          className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-all ${
            activeTab === 'past' 
              ? 'bg-yellow-500 text-black' 
              : 'bg-zinc-800/80 text-zinc-300 hover:bg-zinc-700'
          }`}
        >
          <Clock className={`w-5 h-5 mb-1 ${activeTab === 'past' ? 'text-black' : 'text-zinc-400'}`} />
          <span className="text-xs font-medium">{t('bookings.past')}</span>
          <span className={`text-[10px] mt-0.5 ${activeTab === 'past' ? 'text-black/70' : 'text-zinc-500'}`}>
            ({pastAppointments.length})
          </span>
        </button>
        <button 
          onClick={() => setActiveTab('waitlist')}
          className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-all ${
            activeTab === 'waitlist' 
              ? 'bg-yellow-500 text-black' 
              : 'bg-zinc-800/80 text-zinc-300 hover:bg-zinc-700'
          }`}
        >
          <ListChecks className={`w-5 h-5 mb-1 ${activeTab === 'waitlist' ? 'text-black' : 'text-yellow-400'}`} />
          <span className="text-xs font-medium">{t('bookings.waitlist')}</span>
          {waitingCount > 0 && (
            <span className={`text-[10px] mt-0.5 ${activeTab === 'waitlist' ? 'text-black/70' : 'text-zinc-500'}`}>
              ({waitingCount})
            </span>
          )}
        </button>
      </div>

      {/* Calendar Subscription Banner */}
      {activeTab === 'upcoming' && upcomingAppointments.length > 0 && (
        <div className="bg-zinc-800/50 rounded-xl p-3 border border-zinc-700" data-testid="calendar-subscribe-section">
          {calendarSubscribed ? (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-xs font-medium">{t('bookings.cancelled')}</span>
              <span className="text-zinc-500 text-[10px] ml-auto">{t('bookings.cancelled')}</span>
            </div>
          ) : (
            <div>
              <p className="text-white text-xs font-medium mb-2">{t('common.all')}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSubscribe('google')}
                  className="flex-1 py-1.5 bg-yellow-500 rounded-lg text-black text-[11px] font-medium"
                  data-testid="subscribe-google"
                >
                  Google
                </button>
                <button
                  onClick={() => handleSubscribe('outlook')}
                  className="flex-1 py-1.5 bg-blue-500 rounded-lg text-white text-[11px] font-medium"
                  data-testid="subscribe-outlook"
                >
                  Outlook
                </button>
                <button
                  onClick={() => handleSubscribe('apple')}
                  className="flex-1 py-1.5 bg-zinc-200 rounded-lg text-black text-[11px] font-medium"
                  data-testid="subscribe-apple"
                >
                  Apple
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Appointments List */}
      {(activeTab === 'upcoming' || activeTab === 'past') && (
        <div className="space-y-2">
        {displayAppointments.length === 0 ? (
          <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
            <AlertCircle className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">
              {activeTab === 'upcoming' 
                ? t('bookings.no_upcoming') 
                : t('bookings.no_past')}
            </p>
          </div>
        ) : (
          displayAppointments.map((apt) => {
            const status = getStatusBadge(apt.status);
            const isPast = new Date(apt.start_time) < now;
            
            return (
              <div key={apt.id} className="bg-zinc-800/50 rounded-xl p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white font-medium text-sm">{apt.service_name}</p>
                        <p className="text-zinc-400 text-xs">
                          {apt.trainer_name ? `mit ${apt.trainer_name}` : apt.resource_name || ''}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex gap-3 mt-1.5 text-xs">
                      <span className="text-zinc-300">{formatDate(apt.start_time)}</span>
                      <span className="text-yellow-400">{formatTime(apt.start_time)}</span>
                      {apt.duration_minutes && (
                        <span className="text-zinc-500">{apt.duration_minutes} Min.</span>
                      )}
                    </div>
                    {apt.notes && (
                      <p className="text-zinc-500 text-xs mt-1 italic">{apt.notes}</p>
                    )}
                  </div>
                </div>
                
                {/* Actions for upcoming appointments */}
                {!isPast && apt.status !== 'cancelled' && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button 
                      onClick={() => handleCancel(apt.id)}
                      disabled={cancelling === apt.id}
                      className="flex-1 py-1.5 bg-red-500/20 rounded-lg text-red-400 text-xs disabled:opacity-50"
                      data-testid={`mobile-cancel-${apt.id}`}
                    >
                      {cancelling === apt.id ? t('common.loading') : t('bookings.cancel')}
                    </button>
                    {apt.series_id && (
                      <button 
                        onClick={() => handleCancelSeries(apt.series_id)}
                        disabled={cancellingSeriesId === apt.series_id}
                        className="w-full py-1.5 bg-orange-500/20 rounded-lg text-orange-400 text-xs disabled:opacity-50"
                        data-testid={`mobile-cancel-series-${apt.series_id}`}
                      >
                        {cancellingSeriesId === apt.series_id ? t('common.loading') : t('bookings.cancel_all')}
                      </button>
                    )}
                    <button className="flex-1 py-1.5 bg-zinc-700 rounded-lg text-zinc-200 text-xs font-medium" data-testid={`mobile-details-${apt.id}`}>
                      Details
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Waitlist View */}
      {activeTab === 'waitlist' && (
        <div className="space-y-2">
          {waitlistLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-yellow-500 animate-spin" />
            </div>
          ) : waitlistEntries.length === 0 ? (
            <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
              <ListChecks className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{t('bookings.no_upcoming')}</p>
              <p className="text-zinc-500 text-xs mt-1">
                {t('bookings.waitlist')}
              </p>
            </div>
          ) : (
            waitlistEntries.map(entry => (
              <div 
                key={entry.id || entry._id}
                className={`rounded-xl p-3 ${
                  entry.status === 'waiting' 
                    ? 'bg-yellow-500/10 border border-yellow-500/30' 
                    : entry.status === 'converted'
                    ? 'bg-green-500/10 border border-green-500/30'
                    : 'bg-zinc-800/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <ListChecks className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="text-white font-medium text-sm">
                        {entry.service_name || entry.category_name || 'Training'}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                        entry.status === 'waiting' 
                          ? 'bg-yellow-500/20 text-yellow-400' 
                          : entry.status === 'converted'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-zinc-700 text-zinc-400'
                      }`}>
                        {entry.status === 'waiting' ? t('bookings.status_pending') : 
                         entry.status === 'converted' ? t('bookings.booked') : entry.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {entry.preferred_date 
                          ? new Date(entry.preferred_date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })
                          : 'Flexibel'}
                      </span>
                      <Clock className="w-3 h-3 ml-2" />
                      <span>{formatTimePreferences(entry.time_preferences)}</span>
                    </div>
                    {entry.position && entry.status === 'waiting' && (
                      <p className="text-[10px] text-yellow-500 mt-1">
                        Position {entry.position}
                      </p>
                    )}
                  </div>
                  {entry.status === 'waiting' && (
                    <button
                      onClick={() => handleDeleteWaitlistEntry(entry.id || entry._id)}
                      disabled={deletingEntryId === (entry.id || entry._id)}
                      className="p-2 bg-red-500/20 rounded-lg text-red-400 hover:bg-red-500/30"
                    >
                      {deletingEntryId === (entry.id || entry._id) ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default BookingsScreen;
